import * as React from 'react';

import { IDriverset } from '../api/models';
import { startDayFor } from '../types/IArea';
import { AquaShellFacade, IAquaShellResult } from '../c/AquaShellFacade';
import { GrowthChart } from './GrowthChart';
import type { ISeriesConfig } from './types';

interface IProps {
  driverset: IDriverset | null;
  days: number;
  /** Month the culture period starts in; unset means January. */
  startMonth?: number | null;
  /** AquaShell model code; unset runs the facade's Pacific oyster default. */
  species?: number;
  /** Starting weight in g; unset leaves the facade on its own default. */
  initialWeight?: number;
}

const AquaShellChart = (props: IProps) => {
  const [chart, setChart] = React.useState<IAquaShellResult | null>(null);

  React.useEffect(() => {
    if (props.driverset != null) {
      AquaShellFacade.call(
        { startTime: startDayFor(props.startMonth), endTime: props.days - 1, species: props.species, initialWeight: props.initialWeight },
        props.driverset)
        .then(res => setChart(res));
    }
  }, [props.driverset, props.days, props.startMonth, props.species, props.initialWeight]);

  const getSeries = (): ISeriesConfig[] => {
    if (!chart) return [];

    return [
      {
        label: 'Weight',
        color: '#E63946',
        data: chart.days.map(d => ({ x: d.modelTime, y: d.individualWeight })),
        axis: { position: 'left', title: 'Weight', min: 0, width: 60 },
        tooltipDecimals: 0,
        unit: 'g'
      },
      {
        label: 'Length',
        color: '#0D9488',
        data: chart.days.map(d => ({ x: d.modelTime, y: d.individualLength })),
        axis: { position: 'right', title: 'Length', min: 0, width: 42 },
        tooltipDecimals: 0,
        unit: 'cm'
      },
      {
        label: 'Phytoplankton uptake',
        color: '#7C3AED',
        data: chart.days.map(d => ({ x: d.modelTime, y: d.phytoplanktonUptake })),
        // No fixed max: uptake scales with the driver set's chlorophyll and with
        // the species, so a hard cap flattens the line for anything modest --
        // blue mussel only reaches about 0.10. min stays 0 to keep the baseline
        // anchored rather than exaggerating small variation.
        axis: { position: 'right', title: 'Phyto. uptake', min: 0, width: 50 },
        tooltipDecimals: 3,
        unit: 'µg ind⁻¹ d⁻¹'
      }
    ];
  };

  return (
    <GrowthChart
      days={props.days}
      series={getSeries()}
    />
  );
}

export { AquaShellChart }
